/**
 * SimDiagram — Tier 0 slice-local primitive (SCRUM-167 epic).
 *
 * Renders the Split Inventory Model conflict map per
 * `~/Desktop/design_handoff_properties/SIM_MODEL.md` §3. Rows are
 * units, columns are spaces, ✓ cells mark composition, and the
 * Conflicts column lists units that share ≥1 space with the row's
 * unit.
 *
 * Today the diagram has **no real data source** — the BE has no Space
 * or Unit entity (Property IS the unit). Per the SCRUM-167 plan's
 * "shell with chrome" doctrine for SCRUM-174, this component ships
 * the structural skeleton (semantic `<table>` with proper
 * `<th scope="col"/scope="row">`, ✓ cell styling, Conflicts column
 * chips, Independent badge) so a future contributor sees the
 * canonical layout. The data prop is required, but callers pass a
 * `MOCK_SIM_DEMO` constant from this file when running the SCRUM-174
 * placeholder state — the data shape stays honest (it's still a
 * shell), but the layout + a11y semantics are pinned today.
 *
 * When the BE adds `spaces[]` and `units[]` to `PropertyResponseDto`,
 * callers swap MOCK_SIM_DEMO for the mapped real data; the
 * component contract doesn't change.
 *
 * Accessibility: semantic `<table>` with column + row headers per
 * ACCESSIBILITY.md §9 ("Conflict map, N rows by M columns"; cells
 * carry aria-labels like "composes Main House"). Horizontal-scroll
 * wrapper preserved for narrow viewports (SIM_MODEL.md §3 "the
 * diagram has its own overflow-x: auto wrapper so it scrolls
 * horizontally on narrow viewports without breaking the parent
 * layout").
 */

import * as React from 'react';
import { Badge, Icon } from '@sparta/ui-kit';

export interface SimSpace {
  readonly id: string;
  readonly name: string;
}

export interface SimUnit {
  readonly id: string;
  readonly name: string;
  /** Space ids this unit composes. */
  readonly spaceIds: ReadonlyArray<string>;
}

export interface SimDiagramProps {
  readonly spaces: ReadonlyArray<SimSpace>;
  readonly units: ReadonlyArray<SimUnit>;
  /** Optional id to brand-tint a row (current unit on Unit detail). */
  readonly highlightUnitId?: string;
}

/**
 * Mock demo state used by SCRUM-174 placeholders. Matches the
 * bundle's canonical "Sea Cliff Estate" example so the layout
 * renders with realistic-feeling shape (4 units × 3 spaces with
 * SIM conflicts). Per `SIM_MODEL.md` §1.
 */
export const MOCK_SIM_DEMO: {
  spaces: ReadonlyArray<SimSpace>;
  units: ReadonlyArray<SimUnit>;
} = {
  spaces: [
    { id: 'sp_main', name: 'Main House' },
    { id: 'sp_guest', name: 'Guest House' },
    { id: 'sp_lockoff', name: 'Lock-off Suite' },
  ],
  units: [
    {
      id: 'u_full',
      name: 'Full Estate',
      spaceIds: ['sp_main', 'sp_guest', 'sp_lockoff'],
    },
    { id: 'u_main', name: 'Main House', spaceIds: ['sp_main'] },
    { id: 'u_guest', name: 'Guest House', spaceIds: ['sp_guest'] },
    { id: 'u_lockoff', name: 'Lock-off Suite', spaceIds: ['sp_lockoff'] },
  ],
};

/**
 * Computes which other units share ≥ 1 space with the given unit.
 * Exported so callers can use it for inline conflict warnings (e.g.
 * the wizard's Step 4 "this unit will conflict with N existing
 * units" message). Mirrors `~/Desktop/design_handoff_properties/
 * DATA_MODEL.md` §4 verbatim.
 */
export function computeConflicts(
  unit: SimUnit,
  allUnits: ReadonlyArray<SimUnit>,
): ReadonlyArray<SimUnit> {
  return allUnits.filter(
    (u) =>
      u.id !== unit.id &&
      u.spaceIds.some((s) => unit.spaceIds.includes(s)),
  );
}

export function SimDiagram({
  spaces,
  units,
  highlightUnitId,
}: SimDiagramProps): React.ReactElement {
  return (
    <div className="overflow-x-auto">
      <table
        aria-label="Conflict map"
        className="w-full text-sm"
        style={{ borderCollapse: 'collapse' }}
      >
        <thead>
          <tr>
            <th
              scope="col"
              className="text-left px-3 py-2 text-xs font-bold uppercase"
              style={{
                color: 'var(--fg-tertiary)',
                letterSpacing: '0.08em',
              }}
            >
              Unit
            </th>
            {spaces.map((sp) => (
              <th
                key={sp.id}
                scope="col"
                className="text-left px-3 py-2 text-xs font-bold uppercase"
                style={{
                  color: 'var(--fg-tertiary)',
                  letterSpacing: '0.08em',
                }}
              >
                {sp.name}
              </th>
            ))}
            <th
              scope="col"
              className="text-left px-3 py-2 text-xs font-bold uppercase"
              style={{
                color: 'var(--fg-tertiary)',
                letterSpacing: '0.08em',
              }}
            >
              Conflicts with
            </th>
          </tr>
        </thead>
        <tbody>
          {units.map((unit) => {
            const isHighlighted = unit.id === highlightUnitId;
            const conflicts = computeConflicts(unit, units);
            return (
              <tr
                key={unit.id}
                style={{
                  borderTop: '1px solid var(--border-subtle)',
                  background: isHighlighted
                    ? 'color-mix(in oklab, var(--brand-500) 8%, transparent)'
                    : undefined,
                }}
              >
                <th
                  scope="row"
                  className="text-left px-3 py-2.5 text-sm font-semibold"
                  style={{ color: 'var(--fg-primary)' }}
                >
                  {unit.name}
                </th>
                {spaces.map((sp) => {
                  const composes = unit.spaceIds.includes(sp.id);
                  return (
                    <td
                      key={sp.id}
                      aria-label={
                        composes
                          ? `${unit.name} composes ${sp.name}`
                          : `${unit.name} does not compose ${sp.name}`
                      }
                      className="px-3 py-2.5"
                      style={{
                        background: composes
                          ? 'color-mix(in oklab, var(--brand-500) 14%, transparent)'
                          : undefined,
                      }}
                    >
                      {composes ? (
                        <span
                          aria-hidden="true"
                          className="inline-flex items-center justify-center rounded text-white w-4 h-4"
                          style={{ background: 'var(--brand-500)' }}
                        >
                          <Icon name="check" size={12} />
                        </span>
                      ) : null}
                    </td>
                  );
                })}
                <td className="px-3 py-2.5">
                  {conflicts.length === 0 ? (
                    <Badge variant="success" size="sm">
                      Independent
                    </Badge>
                  ) : (
                    <span className="inline-flex flex-wrap gap-1">
                      {conflicts.map((c) => (
                        <span
                          key={c.id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs"
                          style={{
                            background: 'var(--bg-sunken)',
                            color: 'var(--fg-secondary)',
                          }}
                        >
                          {c.name}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p
        className="mt-3 text-xs"
        style={{ color: 'var(--fg-tertiary)' }}
      >
        ✓ marks the spaces each unit includes. Booking any unit blocks
        every other unit that shares its spaces for those dates.
      </p>
    </div>
  );
}
